import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Carousel, Form, Card, Accordion, Spinner } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaArrowRight, FaCar, FaTruck, FaCarSide, FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaClock, FaInstagram, FaPlay } from 'react-icons/fa';
import FeaturedVehicles from './FeaturedVehicles';
import CustomerReviews from './CustomerReviews';
import FAQTabs from './FAQTabs';
import PaymentCalculator from '../components/PaymentCalculator';
import { getBrands, getModelsByBrand, getBodyTypes } from '../services/vehicleDataService';
import { standardizeHomePageFilters, filtersToUrlParams } from '../utils/filterUtils';
import SalesSystem from './SalesSystem';

import './HomePage.css';

const HomePage = () => {
    // Navigation hook ekle
    const navigate = useNavigate();
    const location = useLocation();

    // Carousel kontrolü için state
    const [index, setIndex] = useState(0);

    // Instagram Reels için state
    const [instagramReels, setInstagramReels] = useState([]);
    const [loadingReels, setLoadingReels] = useState(false);

    // Filtre değerleri için state
    const [filters, setFilters] = useState({
        type: '',
        brand: '',
        model: '',
        transmission: '',
        minPrice: '',
        maxPrice: '',
        minYear: '',
        maxYear: '',
        minKm: '',
        maxKm: ''
    });
    const [brands, setBrands] = useState([]);
    const [models, setModels] = useState([]);
    const [bodyTypes, setBodyTypes] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(true);
    const [loadingModels, setLoadingModels] = useState(false);
    const [loadingBodyTypes, setLoadingBodyTypes] = useState(true);

    // Seçili araç tipi için state
    const [selectedCarType, setSelectedCarType] = useState('');

    // Instagram Reels yükle
    const fetchInstagramReels = async () => {
        setLoadingReels(true);
        try {
            // Bu örnekte mock data kullanıyoruz - gerçek implementasyonda 
            // Instagram Graph API veya güvenli bir backend endpoint kullanılmalı
            const mockReels = [
                {
                    id: '1',
                    media_url: '/images/reel-thumb-1.jpg',
                    permalink: 'https://www.instagram.com/reel/ABC123/',
                    caption: '🚗 Hayalindeki araba artık çok yakın! #otoprime #araç #taksit',
                    thumbnail_url: '/images/reel-thumb-1.jpg'
                },
                {
                    id: '2',
                    media_url: '/images/reel-thumb-2.jpg',
                    permalink: 'https://www.instagram.com/reel/DEF456/',
                    caption: '✨ 36 aya varan taksit fırsatları! #araçkredi #otoprime',
                    thumbnail_url: '/images/reel-thumb-2.jpg'
                },
                {
                    id: '3',
                    media_url: '/images/reel-thumb-3.jpg',
                    permalink: 'https://www.instagram.com/reel/GHI789/',
                    caption: '🔥 Bu ay özel kampanyalar! #araç #kampanya #otoprime',
                    thumbnail_url: '/images/reel-thumb-3.jpg'
                },
                {
                    id: '4',
                    media_url: '/images/reel-thumb-4.jpg',
                    permalink: 'https://www.instagram.com/reel/JKL012/',
                    caption: '🎯 Müşteri yorumları gelmeye devam ediyor! #memnuniyet',
                    thumbnail_url: '/images/reel-thumb-4.jpg'
                }
            ];
            
            // Gerçek API implementasyonu için:
            // const response = await fetch('/api/instagram-reels');
            // const data = await response.json();
            // setInstagramReels(data);
            
            setInstagramReels(mockReels);
        } catch (error) {
            console.error('Instagram reels yükleme hatası:', error);
        } finally {
            setLoadingReels(false);
        }
    };

    // Component yüklendiğinde
    useEffect(() => {
        // Carousel göstergeleri
        setupCarouselIndicators();

        // Araç tipi butonları
        setupCarTypeButtons();

        const fetchVehicleData = async () => {
            try {
                setLoadingBrands(true);
                setLoadingBodyTypes(true);

                const brandsResponse = await getBrands();
                if (brandsResponse.success) {
                    setBrands(brandsResponse.data);
                }

                const typesResponse = await getBodyTypes();
                if (typesResponse.success) {
                    setBodyTypes(typesResponse.data);
                }
            } catch (error) {
                console.error('Veri yükleme hatası:', error);
            } finally {
                setLoadingBrands(false);
                setLoadingBodyTypes(false);
            }
        };

        fetchVehicleData();
        fetchInstagramReels();

        // URL parametrelerini kontrol et ve gerekirse scroll yap
        const searchParams = new URLSearchParams(location.search);
        const scrollToSection = searchParams.get('scrollTo');

        if (scrollToSection) {
            setTimeout(() => {
                const element = document.getElementById(scrollToSection);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }, 500); // Sayfanın tam yüklenmesi için biraz beklet
        }
    }, [location]);

    // Marka değiştiğinde modelleri yükle
    useEffect(() => {
        if (filters.brand) {
            const fetchModels = async () => {
                try {
                    setLoadingModels(true);
                    const modelsResponse = await getModelsByBrand(filters.brand);
                    if (modelsResponse.success) {
                        setModels(modelsResponse.data);
                    }
                } catch (error) {
                    console.error(`${filters.brand} markasına ait modeller yüklenirken hata:`, error);
                    setModels([]);
                } finally {
                    setLoadingModels(false);
                }
            };

            fetchModels();
        } else {
            setModels([]);
        }
    }, [filters.brand]);

    // Carousel index değişikliği
    const handleSelect = (selectedIndex) => {
        setIndex(selectedIndex);
    };

    // Filtre değişikliklerini takip et
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters({
            ...filters,
            [name]: value
        });
    };

    // Araç tipi seçimi
    const handleCarTypeSelect = (type) => {
        setSelectedCarType(type);
        setFilters({
            ...filters,
            type: type
        });
    };

    // Filtre formunu gönder
    const handleSubmitFilter = (e) => {
        e.preventDefault();

        // Filtre nesnemizi standardize et (type -> bodyType dönüşümü dahil)
        const standardizedFilters = standardizeHomePageFilters(filters);

        // URL parametrelerini oluştur, boş değerleri filtrele
        const queryParams = filtersToUrlParams(standardizedFilters).toString();

        // Vehicles sayfasına yönlendir
        navigate(`/vehicles?${queryParams}`);
    };

    // Carousel göstergeleri
    const setupCarouselIndicators = () => {
        const carouselElement = document.getElementById('mainCarousel');
        const customIndicators = document.querySelectorAll('.carousel-indicators-custom button');

        if (carouselElement && customIndicators.length > 0) {
            customIndicators.forEach((indicator, idx) => {
                indicator.addEventListener('click', () => {
                    setIndex(idx);
                });
            });
        }
    };

    // Araç tipi butonları
    const setupCarTypeButtons = () => {
        const carTypeButtons = document.querySelectorAll('.car-type-btn');

        if (carTypeButtons.length > 0) {
            carTypeButtons.forEach(button => {
                button.addEventListener('click', function () {
                    // Önce tüm butonlardan active sınıfını kaldır
                    carTypeButtons.forEach(btn => {
                        btn.classList.remove('btn-primary');
                        btn.classList.add('btn-outline-secondary');
                    });

                    // Tıklanan butonu aktif yap
                    this.classList.remove('btn-outline-secondary');
                    this.classList.add('btn-primary');
                });
            });
        }
    };

    // Carousel öğeleri - Daha çarpıcı içeriklerle güncellendi
    const carouselItems = [
        {
            id: 1,
            title: "Hayalinizdeki Araba Sadece Birkaç Tık Uzakta!",
            description: "36 aya varan esnek taksit seçenekleriyle istediğiniz aracı hemen alın. Hiçbir evrak yok, sadece hayaller gerçek oluyor!",
            image: "/images/car1.png",
            buttonText: "Hemen İncele",
            buttonLink: "/sales-system",
            bgColor: "linear-gradient(135deg, #ffffff 0%, #ffcc00 100%)"
        },
        {
            id: 2,
            title: "En Kolay Araç Alma Platformu, 4 dakika!",
            description: "Binlerce araç seçeneği, anında onay, kapıda teslimat! Sen sadece beğen, biz gerisini halledelim.",
            image: "/images/car2.png",
            buttonText: "Şimdi Başvur",
            buttonLink: "/application",
            bgColor: "linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)"
        },
        {
            id: 3,
            title: "Artık Beklemek Yok! Hemen Sahip Ol!",
            description: "Geleneksel kredi başvurularını unut! Otoprime ile aracın 24 saat içinde kapında olabilir.",
            image: "/images/car3.png",
            buttonText: "Keşfet",
            buttonLink: "/application",
            bgColor: "linear-gradient(135deg, #ffffff 0%, #ffcc2d 100%)"
        }
    ];

    return (
        <div className="homepage">
            {/* Ana Carousel Bölümü */}
            <div className="main-carousel" id="mainCarousel">
                <Carousel
                    activeIndex={index}
                    onSelect={handleSelect}
                    interval={5000}
                    controls={true}
                    indicators={true}
                    className="split-carousel"
                >
                    {carouselItems.map((item) => (
                        <Carousel.Item key={item.id}>
                            <div className="carousel-content" style={{ background: item.bgColor }}>
                                <Container>
                                    <Row className="align-items-center">
                                        <Col md={6} className="carousel-text">
                                            <h1>{item.title}</h1>
                                            <p>{item.description}</p>
                                            <Link to={item.buttonLink} className="btn btn-primary">{item.buttonText}</Link>
                                        </Col>
                                        <Col md={6} className="carousel-image">
                                            <img
                                                src={item.image}
                                                alt={item.title}
                                                className="d-block img-fluid"
                                            />
                                        </Col>
                                    </Row>
                                </Container>
                            </div>
                        </Carousel.Item>
                    ))}
                </Carousel>
                <div className="carousel-indicators-custom">
                    {carouselItems.map((_, idx) => (
                        <button
                            key={idx}
                            className={index === idx ? "active" : ""}
                            onClick={() => setIndex(idx)}
                        />
                    ))}
                </div>
            </div>

            {/* Aylık Ödeme Hesaplama */}
            <section className="payment-calculator-section py-5 bg-light">
                <Container>
                    <Row>
                        <Col md={6}>
                            <Card className="calculator-card border-0 shadow-sm">
                                <Card.Header>
                                    <h5 className="mb-0">Aylık Ödeme Tutarınız</h5>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <PaymentCalculator initialAmount={100000} />
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6} className="d-none d-md-flex align-items-center justify-content-center">
                            <img
                                src="/images/hesaplama.png"
                                alt="Ödeme Hesaplama"
                                className="img-fluid calculator-image"
                                style={{ maxHeight: '550px' }}
                            />
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Öne Çıkan Araçlar ILERDE ACILACAK */}
            {/*<FeaturedVehicles />*/}

            {/* Hızlı Filtre Bölümü - ILERDE ACILACAK */}
            {/*<section className="quick-filter py-5 bg-light" id="quick-filter-section">*/}
            {/*    <Container>*/}
            {/*        <Row className="align-items-center">*/}
            {/*            <Col lg={4} className="mb-4 mb-lg-0 text-center">*/}
            {/*                <img*/}
            {/*                    src="/images/person-search.png"*/}
            {/*                    alt="Filtre"*/}
            {/*                    className="img-fluid d-lg-block mx-auto"*/}
            {/*                    style={{ maxHeight: '600px' }}*/}
            {/*                />*/}
            {/*            </Col>*/}
            {/*            <Col lg={8}>*/}
            {/*                <div className="filter-panel p-4 bg-white shadow-sm rounded">*/}
            {/*                    <Form onSubmit={handleSubmitFilter}>*/}
            {/*                        <div className="mb-4">*/}
            {/*                            <Form.Label>Araç Tipi</Form.Label>*/}
            {/*                            <Form.Select*/}
            {/*                                name="type"*/}
            {/*                                value={filters.type}*/}
            {/*                                onChange={handleFilterChange}*/}
            {/*                                disabled={loadingBodyTypes}*/}
            {/*                                className="mb-3"*/}
            {/*                            >*/}
            {/*                                <option value="">Tüm Araç Tipleri</option>*/}
            {/*                                {bodyTypes.map(type => (*/}
            {/*                                    <option key={type} value={type.toLowerCase()}>*/}
            {/*                                        {type}*/}
            {/*                                    </option>*/}
            {/*                                ))}*/}
            {/*                            </Form.Select>*/}
            {/*                        </div>*/}

            {/*                        <Row className="g-3">*/}
            {/*                            <Col md={6}>*/}
            {/*                                <Form.Group className="mb-3">*/}
            {/*                                    <Form.Label>Marka</Form.Label>*/}
            {/*                                    <Form.Select*/}
            {/*                                        name="brand"*/}
            {/*                                        value={filters.brand}*/}
            {/*                                        onChange={handleFilterChange}*/}
            {/*                                        disabled={loadingBrands}*/}
            {/*                                    >*/}
            {/*                                        <option value="">Tüm Markalar</option>*/}
            {/*                                        {brands.map(brand => (*/}
            {/*                                            <option key={brand} value={brand}>*/}
            {/*                                                {brand}*/}
            {/*                                            </option>*/}
            {/*                                        ))}*/}
            {/*                                    </Form.Select>*/}
            {/*                                </Form.Group>*/}
            {/*                            </Col>*/}

            {/*                            <Col md={6}>*/}
            {/*                                <Form.Group className="mb-3">*/}
            {/*                                    <Form.Label>Model</Form.Label>*/}
            {/*                                    <Form.Select*/}
            {/*                                        name="model"*/}
            {/*                                        value={filters.model || ''}*/}
            {/*                                        onChange={handleFilterChange}*/}
            {/*                                        disabled={!filters.brand || loadingModels}*/}
            {/*                                    >*/}
            {/*                                        <option value="">Tüm Modeller</option>*/}
            {/*                                        {models.map(model => (*/}
            {/*                                            <option key={model.name} value={model.name}>*/}
            {/*                                                {model.name}*/}
            {/*                                            </option>*/}
            {/*                                        ))}*/}
            {/*                                    </Form.Select>*/}
            {/*                                </Form.Group>*/}
            {/*                            </Col>*/}
            {/*                        </Row>*/}

            {/*                        <Row className="mb-3">*/}
            {/*                            <Form.Label>Fiyat</Form.Label>*/}
            {/*                            <Col md={6}>*/}
            {/*                                <Form.Control*/}
            {/*                                    type="number"*/}
            {/*                                    placeholder="Min. ₺"*/}
            {/*                                    name="minPrice"*/}
            {/*                                    value={filters.minPrice}*/}
            {/*                                    onChange={handleFilterChange}*/}
            {/*                                    className="mb-3 mb-md-0"*/}
            {/*                                />*/}
            {/*                            </Col>*/}
            {/*                            <Col md={6}>*/}
            {/*                                <Form.Control*/}
            {/*                                    type="number"*/}
            {/*                                    placeholder="Maks. ₺"*/}
            {/*                                    name="maxPrice"*/}
            {/*                                    value={filters.maxPrice}*/}
            {/*                                    onChange={handleFilterChange}*/}
            {/*                                />*/}
            {/*                            </Col>*/}
            {/*                        </Row>*/}

            {/*                        <Row className="mb-3">*/}
            {/*                            <Form.Label>Model Yılı</Form.Label>*/}
            {/*                            <Col md={6}>*/}
            {/*                                <Form.Control*/}
            {/*                                    type="number"*/}
            {/*                                    placeholder="Min. Yıl"*/}
            {/*                                    name="minYear"*/}
            {/*                                    value={filters.minYear}*/}
            {/*                                    onChange={handleFilterChange}*/}
            {/*                                    className="mb-3 mb-md-0"*/}
            {/*                                />*/}
            {/*                            </Col>*/}
            {/*                            <Col md={6}>*/}
            {/*                                <Form.Control*/}
            {/*                                    type="number"*/}
            {/*                                    placeholder="Maks. Yıl"*/}
            {/*                                    name="maxYear"*/}
            {/*                                    value={filters.maxYear}*/}
            {/*                                    onChange={handleFilterChange}*/}
            {/*                                />*/}
            {/*                            </Col>*/}
            {/*                        </Row>*/}

            {/*                        <div className="d-grid gap-2 mt-4">*/}
            {/*                            <Button variant="primary" type="submit" className="search-btn py-2">*/}
            {/*                                Sonuçları Gör <FaArrowRight className="ms-2" />*/}
            {/*                            </Button>*/}
            {/*                        </div>*/}
            {/*                    </Form>*/}
            {/*                </div>*/}
            {/*            </Col>*/}
            {/*        </Row>*/}
            {/*    </Container>*/}
            {/*</section>*/}

            {/* Nasıl Çalışır Bölümü */}
            <SalesSystem
                showBanner={false}
                showStepsTitle={true}
                showAdvantages={false}
                showFAQ={false}
                showCTA={false}
            />

            {/* Müşteri Yorumları */}
            <CustomerReviews />

            {/* Otoprime Hakkında - SSS Bölümü */}
            <FAQTabs
                showTitle={true}
                title="Sorularla Otoprime'i Tanı"
                id="home-faq-section"
                defaultActiveTab="general"
            />

            {/* Instagram Reels Bölümü */}
            <section className="instagram-reels-section py-5 bg-light">
                <Container>
                    <div className="text-center mb-5">
                        <h2 className="section-title">
                            <FaInstagram className="me-3" style={{ color: '#E4405F' }} />
                            Instagram'da Bizi Takip Edin
                        </h2>
                        <p className="lead">
                            Güncel kampanyalar, müşteri hikayeleri ve araç önerilerini kaçırmayın!
                        </p>
                        <a 
                            href="https://www.instagram.com/otoprimetaksit/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn btn-primary mb-4"
                        >
                            <FaInstagram className="me-2" />
                            @otoprimetaksit
                        </a>
                    </div>

                    {loadingReels ? (
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Instagram içerikleri yükleniyor...</p>
                        </div>
                    ) : (
                        <Row className="g-4">
                            {instagramReels.map((reel) => (
                                <Col key={reel.id} lg={3} md={4} sm={6}>
                                    <div className="instagram-reel-card">
                                        <div className="reel-image-container">
                                            <img
                                                src={reel.thumbnail_url}
                                                alt={reel.caption}
                                                className="reel-image"
                                            />
                                            <div className="reel-overlay">
                                                <a
                                                    href={reel.permalink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="reel-play-btn"
                                                >
                                                    <FaPlay />
                                                </a>
                                            </div>
                                        </div>
                                        <div className="reel-caption">
                                            <p>{reel.caption}</p>
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    )}

                    <div className="text-center mt-5">
                        <a 
                            href="https://www.instagram.com/otoprimetaksit/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn btn-outline-primary btn-lg"
                        >
                            <FaInstagram className="me-2" />
                            Daha Fazla İçerik İçin Takip Et
                        </a>
                    </div>
                </Container>
            </section>
        </div>
    );
};

export default HomePage;